import React from 'react';
import styled from 'styled-components';
import { useQuiz } from '../../context/QuizContext';

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.background.paper};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin: ${({ theme }) => theme.spacing.md};
  box-shadow: ${({ theme }) => theme.shadows.small};
  overflow: hidden;
  flex: 1;
  min-width: 0; /* Allow flexbox to shrink */
`;

const ExplanationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.medium};
`;

const ExplanationTitle = styled.h3`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const ContentContainer = styled.div`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.md};
  overflow-y: auto;
`;

const ExplanationSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SectionTitle = styled.h4`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  padding-bottom: ${({ theme }) => theme.spacing.xs};
`;

const OptionExplanation = styled.div<{ isCorrect: boolean }>`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background-color: ${({ theme, isCorrect }) => 
    isCorrect 
      ? `${theme.colors.status.correct}20` 
      : `${theme.colors.status.incorrect}20`};
  border-left: 4px solid ${({ theme, isCorrect }) => 
    isCorrect 
      ? theme.colors.status.correct 
      : theme.colors.status.incorrect};
`;

const OptionHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const OptionLabel = styled.div<{ isCorrect: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-weight: bold;
  color: ${({ theme, isCorrect }) => 
    isCorrect 
      ? theme.colors.status.correct 
      : theme.colors.status.incorrect};
`;

const OptionText = styled.p`
  margin-left: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ExplanationText = styled.p`
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.5;
`;

const AdditionalInfo = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const BulletPoints = styled.ul`
  padding-left: ${({ theme }) => theme.spacing.lg};
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const BulletPoint = styled.li`
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.5;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  position: relative;
  
  &::before {
    content: '✓';
    position: absolute;
    left: -${({ theme }) => theme.spacing.lg};
    color: ${({ theme }) => theme.colors.status.correct};
  }
`;

const VideoSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const VideoCard = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background-color: ${({ theme }) => theme.colors.background.input};
  margin-top: ${({ theme }) => theme.spacing.md};
  cursor: pointer;
  transition: background-color ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.border.light};
  }
`;

const ThumbnailContainer = styled.div`
  position: relative;
  width: 120px;
  height: 68px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  overflow: hidden;
  margin-right: ${({ theme }) => theme.spacing.md};
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PlayButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  padding: 8px;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const VideoInfo = styled.div`
  flex: 1;
`;

const VideoTitle = styled.div`
  font-weight: bold;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const VideoMeta = styled.div`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const TopicBadge = styled.div`
  background-color: ${({ theme }) => theme.colors.secondary};
  color: white;
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: 0.8rem;
  font-weight: bold;
`;

const StatusIcon = styled.span`
  font-size: 1.5rem;
  margin-right: ${({ theme }) => theme.spacing.sm};
`;

const ExplanationPanel: React.FC = () => {
  const { quiz, quizState } = useQuiz();
  
  if (!quiz) {
    return <PanelContainer>Loading...</PanelContainer>;
  }
  
  return (
    <PanelContainer>
      <ExplanationHeader>
        <ExplanationTitle>Explanation</ExplanationTitle>
      </ExplanationHeader>
      
      <ContentContainer>
        {!quizState.isAnswered ? (
          <ExplanationText>
            Submit your answer to see the explanation.
          </ExplanationText>
        ) : (
          <>
            <ExplanationSection>
              <SectionTitle>Explanation</SectionTitle>
              
              {quiz.explanation.map((exp) => (
                <OptionExplanation key={exp.option} isCorrect={exp.isCorrect}>
                  <OptionHeader>
                    <OptionLabel isCorrect={exp.isCorrect}>
                      <StatusIcon>
                        {exp.isCorrect ? '✅' : '❌'}
                      </StatusIcon>
                    </OptionLabel>
                    <OptionText>
                      Option {exp.option}
                    </OptionText>
                  </OptionHeader>
                  <ExplanationText>
                    {exp.text}
                  </ExplanationText>
                </OptionExplanation>
              ))}
            </ExplanationSection>
            
            {quiz.additionalInfo.points.length > 0 && (
              <AdditionalInfo>
                <SectionTitle>Additional Info</SectionTitle>
                <BulletPoints>
                  {quiz.additionalInfo.points.map((point, index) => (
                    <BulletPoint key={index}>{point}</BulletPoint>
                  ))}
                </BulletPoints>
              </AdditionalInfo>
            )}
            
            {quiz.relatedVideos.length > 0 && (
              <VideoSection>
                <SectionTitle>Related Videos</SectionTitle>
                
                {quiz.relatedVideos.map((video, index) => (
                  <VideoCard key={index}>
                    <ThumbnailContainer>
                      <Thumbnail src={video.thumbnail} alt={video.videoTitle} />
                      <PlayButton>
                        ▶️
                      </PlayButton>
                    </ThumbnailContainer>
                    
                    <VideoInfo>
                      <VideoTitle>{video.videoTitle}</VideoTitle>
                      <VideoMeta>
                        <span>{video.duration}</span>
                        <TopicBadge>{video.subTopicTitle}</TopicBadge>
                      </VideoMeta>
                    </VideoInfo>
                  </VideoCard>
                ))}
              </VideoSection>
            )}
          </>
        )}
      </ContentContainer>
    </PanelContainer>
  );
};

export default ExplanationPanel; 